/**
 * School Analytics - Tab 5 of School Admin Dashboard
 *
 * Displays ROI metrics and learning analytics:
 * - Total learning time across school
 * - Cost per learning hour
 * - Most common questions (from student_questions table)
 * - Topics students struggle with
 * - Engagement trends
 * - ROI calculations
 *
 * Data Source: GET /api/admin/school/analytics (session-based - no ID)
 */

import React, { useState, useEffect } from 'react';
import axios from '../utils/authInterceptor';

// Tappy Brand Colors - Official 2025 Design System
const TappyBrandColors = {
  primary: {
    darkTeal: '#1A3E48',
    teal: '#297A92',
    mediumTeal: '#388899',
    brandBlue: '#76909F',
    brightTeal: '#42909E',
    aqua: '#4D99A5',
    lightAqua: '#5BA3AC',
    skyBlue: '#6DB4CD',
  },
  accent: {
    coral: '#EA6D37',
    gold: '#ECC962',
    brightGold: '#F8C341',
    tan: '#C59F65',
  },
  neutral: {
    darkGray: '#3D4954',
    mediumGray: '#8D7F88',
    lightTeal: '#ABCACA',
    paleGray: '#D5DEDD',
    lightGray: '#ECF3F2',
    veryLight: '#F5F9FA',
    almostWhite: '#FCFDFE',
  },
  semantic: {
    success: '#42909E',
    warning: '#ECC962',
    error: '#EA6D37',
    info: '#6DB4CD',
  }
};

function SchoolAnalytics({ getApiUrl, isDistrictAdminView }) {
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('30d'); // 7d, 30d, 90d, year

  // Energy-based learning data
  const [teachingQuality, setTeachingQuality] = useState(null);
  const [reteachROI, setReteachROI] = useState(null);
  const [energyLoading, setEnergyLoading] = useState(false);

  // Helper to get API URL - use prop if provided, otherwise default to session-based
  const buildApiUrl = (endpoint) => {
    if (getApiUrl) {
      return getApiUrl(endpoint);
    }
    return `/api/admin/school/${endpoint}`;
  };

  useEffect(() => {
    fetchAnalytics();
    fetchEnergyData();
  }, [timeRange]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${buildApiUrl('analytics')}?period=${timeRange}`);
      setAnalytics(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError(err.response?.data?.error || 'Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const fetchEnergyData = async () => {
    try {
      setEnergyLoading(true);

      // Fetch teaching quality and re-teach ROI in parallel
      const [qualityResponse, roiResponse] = await Promise.all([
        axios.get(buildApiUrl('teaching-quality')),
        axios.get(buildApiUrl('reteach-roi'))
      ]);

      setTeachingQuality(qualityResponse.data);
      setReteachROI(roiResponse.data);
    } catch (err) {
      console.error('Error fetching energy data:', err);
      // Don't set main error - these are optional enhancements
    } finally {
      setEnergyLoading(false);
    }
  };

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px' }}>
        <div style={{
          width: '48px',
          height: '48px',
          margin: '0 auto 20px',
          borderRadius: '50%',
          backgroundColor: TappyBrandColors.accent.coral + '15',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '24px',
          color: TappyBrandColors.accent.coral
        }}>!</div>
        <h3 style={{ color: TappyBrandColors.accent.coral, marginBottom: '12px', fontWeight: 600 }}>Error Loading Analytics</h3>
        <p style={{ color: TappyBrandColors.neutral.mediumGray, marginBottom: '20px' }}>{error}</p>
        <button
          onClick={fetchAnalytics}
          style={{
            padding: '10px 20px',
            backgroundColor: TappyBrandColors.primary.teal,
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: 500
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px' }}>
        <div style={{
          display: 'inline-block',
          width: '40px',
          height: '40px',
          border: `3px solid ${TappyBrandColors.neutral.lightGray}`,
          borderTop: `3px solid ${TappyBrandColors.primary.teal}`,
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          marginBottom: '20px'
        }} />
        <p style={{ color: TappyBrandColors.neutral.mediumGray }}>Loading analytics...</p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px' }}>
        <div style={{
          width: '48px',
          height: '48px',
          margin: '0 auto 20px',
          borderRadius: '12px',
          backgroundColor: TappyBrandColors.neutral.lightGray,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '20px',
          color: TappyBrandColors.neutral.mediumGray
        }}>□</div>
        <p style={{ color: TappyBrandColors.neutral.mediumGray }}>No analytics data available</p>
      </div>
    );
  }

  const { metrics, common_questions, struggling_topics, roi } = analytics;

  return (
    <div>
      {/* Page Header */}
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h2 style={{ fontSize: '24px', fontWeight: 600, color: '#1f2937', marginBottom: '8px' }}>
            Learning Analytics & ROI
          </h2>
          <p style={{ fontSize: '14px', color: '#6b7280' }}>
            Impact metrics and return on investment analysis
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {['7d', '30d', '90d', 'year'].map(range => (
            <button
              key={range}
              onClick={() => setTimeRange(range)}
              style={{
                padding: '8px 16px',
                backgroundColor: timeRange === range ? TappyBrandColors.primary.teal : 'white',
                color: timeRange === range ? 'white' : TappyBrandColors.neutral.darkGray,
                border: '1px solid',
                borderColor: timeRange === range ? TappyBrandColors.primary.teal : TappyBrandColors.neutral.paleGray,
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 500,
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            >
              {range === 'year' ? '1 Year' : range.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {/* ROI Metrics */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: '20px',
        marginBottom: '32px'
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '28px',
          borderRadius: '12px',
          border: '1px solid #e5e7eb',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px', fontWeight: 500 }}>
            Total Learning Time
          </div>
          <div style={{ fontSize: '36px', fontWeight: 700, color: '#4F46E5', marginBottom: '4px' }}>
            {metrics?.total_hours?.toLocaleString() || 0}
          </div>
          <div style={{ fontSize: '13px', color: '#9ca3af' }}>hours</div>
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '28px',
          borderRadius: '12px',
          border: '1px solid #e5e7eb',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px', fontWeight: 500 }}>
            Topics Mastered
          </div>
          <div style={{ fontSize: '36px', fontWeight: 700, color: '#10b981', marginBottom: '4px' }}>
            {metrics?.topics_mastered?.toLocaleString() || 0}
          </div>
          <div style={{ fontSize: '13px', color: '#9ca3af' }}>
            Avg mastery: {metrics?.avg_topic_mastery_pct || 0}%
          </div>
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '28px',
          borderRadius: '12px',
          border: '1px solid #e5e7eb',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px', fontWeight: 500 }}>
            Avg Hours Per Student
          </div>
          <div style={{ fontSize: '36px', fontWeight: 700, color: '#f59e0b', marginBottom: '4px' }}>
            {metrics?.avg_hours_per_student?.toFixed(1) || '0.0'}
          </div>
          <div style={{ fontSize: '13px', color: '#9ca3af' }}>hours per student</div>
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '28px',
          borderRadius: '12px',
          border: '1px solid #e5e7eb',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px', fontWeight: 500 }}>
            Questions Asked
          </div>
          <div style={{ fontSize: '36px', fontWeight: 700, color: '#8b5cf6', marginBottom: '4px' }}>
            {metrics?.total_questions?.toLocaleString() || 0}
          </div>
          <div style={{ fontSize: '13px', color: '#9ca3af' }}>student questions</div>
        </div>
      </div>

      {/* ROI Breakdown */}
      {roi && (
        <div style={{
          backgroundColor: 'white',
          padding: '32px',
          borderRadius: '12px',
          border: '1px solid #e5e7eb',
          marginBottom: '32px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#1f2937', marginBottom: '24px' }}>
            Return on Investment Analysis
          </h3>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px' }}>
            <div>
              <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px', textTransform: 'uppercase', fontWeight: 600 }}>
                Annual License Cost
              </div>
              <div style={{ fontSize: '28px', fontWeight: 700, color: '#1f2937' }}>
                ${roi.annual_cost?.toLocaleString() || 0}
              </div>
              <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
                {roi.active_students ? `$${roi.cost_per_active_student?.toLocaleString() || 0} per active student` : ''}
              </div>
            </div>

            <div>
              <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px', textTransform: 'uppercase', fontWeight: 600 }}>
                Intervention Cost Avoided
              </div>
              <div style={{ fontSize: '28px', fontWeight: 700, color: '#10b981' }}>
                ${roi.intervention_comparison_cost?.toLocaleString() || 0}
              </div>
              <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
                At ${roi.intervention_specialist_rate || 70}/hour for intervention specialists
              </div>
            </div>

            <div>
              <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px', textTransform: 'uppercase', fontWeight: 600 }}>
                Teacher Capacity Saved
              </div>
              <div style={{ fontSize: '28px', fontWeight: 700, color: '#10b981' }}>
                {roi.teacher_time_saved_hours?.toFixed(1) || 0}h
              </div>
              <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
                ${roi.teacher_time_value?.toLocaleString() || 0} value (${roi.teacher_hourly_rate || 45}/hour)
              </div>
            </div>
          </div>

          <div style={{
            marginTop: '24px',
            padding: '20px',
            backgroundColor: '#f0fdf4',
            borderRadius: '8px',
            border: '1px solid #22c55e'
          }}>
            <div style={{ fontSize: '14px', color: '#15803d', lineHeight: '1.8' }}>
              <strong>Smart Investment, Proven Results:</strong> Your school delivered <strong>{metrics?.total_hours?.toLocaleString() || 0} hours</strong> of personalized learning at <strong>${roi.cost_per_active_student?.toLocaleString() || 0} per active student</strong>—replacing expensive intervention programs that would cost <strong>${roi.intervention_comparison_cost?.toLocaleString() || 0}</strong> at specialist rates.
              {roi.teacher_time_saved_hours > 0 && (
                <span> Tappy also saved <strong>{roi.teacher_time_saved_hours?.toFixed(1)}h of teacher time</strong> by catching misconceptions early, keeping your best teachers focused on instruction instead of remediation.</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Teaching Effectiveness Overview (Energy-Based Learning) */}
      {teachingQuality && teachingQuality.summary && (
        <div style={{
          backgroundColor: 'white',
          padding: '32px',
          borderRadius: '12px',
          border: '1px solid #e5e7eb',
          marginBottom: '32px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#1f2937', marginBottom: '8px' }}>
            Teaching Effectiveness Overview
          </h3>
          <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '24px' }}>
            Energy-based analytics showing which teaching approaches work naturally for your students
          </p>

          {/* Summary Metrics */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px', marginBottom: '28px' }}>
            <div style={{
              padding: '20px',
              backgroundColor: teachingQuality.summary.avg_energy < 0.4 ? '#f0fdf4' : teachingQuality.summary.avg_energy < 0.6 ? '#fef9e7' : '#fef2f2',
              borderRadius: '8px',
              border: '1px solid',
              borderColor: teachingQuality.summary.avg_energy < 0.4 ? '#22c55e' : teachingQuality.summary.avg_energy < 0.6 ? '#eab308' : '#ef4444'
            }}>
              <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px', fontWeight: 600 }}>
                Average Energy Score
              </div>
              <div style={{
                fontSize: '32px',
                fontWeight: 700,
                color: teachingQuality.summary.avg_energy < 0.4 ? TappyBrandColors.semantic.success : teachingQuality.summary.avg_energy < 0.6 ? TappyBrandColors.semantic.warning : TappyBrandColors.semantic.error
              }}>
                {teachingQuality.summary.avg_energy}
              </div>
              <div style={{ fontSize: '12px', color: TappyBrandColors.neutral.mediumGray, marginTop: '4px' }}>
                {teachingQuality.summary.avg_energy < 0.4 ? 'Teaching landing naturally' : teachingQuality.summary.avg_energy < 0.6 ? 'Room for improvement' : 'Needs adjustment'}
              </div>
            </div>

            <div style={{ padding: '20px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
              <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px', fontWeight: 600 }}>
                Effective Actions
              </div>
              <div style={{ fontSize: '32px', fontWeight: 700, color: '#4F46E5' }}>
                {teachingQuality.summary.effective_actions_pct}%
              </div>
              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                of {teachingQuality.summary.total_actions} teaching actions
              </div>
            </div>

            <div style={{ padding: '20px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
              <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px', fontWeight: 600 }}>
                Avg Mastery Gain
              </div>
              <div style={{ fontSize: '32px', fontWeight: 700, color: '#10b981' }}>
                +{(teachingQuality.performance.avg_mastery_gain * 100).toFixed(1)}%
              </div>
              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                per teaching interaction
              </div>
            </div>
          </div>

          {/* Approach Breakdown */}
          <div>
            <h4 style={{ fontSize: '15px', fontWeight: 600, color: '#1f2937', marginBottom: '16px' }}>
              Teaching Approaches by Effectiveness
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {teachingQuality.by_approach && teachingQuality.by_approach.slice(0, 5).map((approach, index) => (
                <div
                  key={index}
                  style={{
                    padding: '16px 20px',
                    backgroundColor: approach.energy < 0.4 ? '#f0fdf4' : approach.energy < 0.6 ? '#fef9e7' : '#fef2f2',
                    borderRadius: '8px',
                    border: '1px solid',
                    borderColor: approach.energy < 0.4 ? '#86efac' : approach.energy < 0.6 ? '#fde047' : '#fca5a5',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '14px', fontWeight: 600, color: '#1f2937', marginBottom: '4px', textTransform: 'capitalize' }}>
                      {approach.approach.replace(/_/g, ' ')}
                    </div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>
                      Used in {approach.usage_pct}% of teaching interactions ({approach.count} times)
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '2px' }}>Energy</div>
                      <div style={{
                        fontSize: '20px',
                        fontWeight: 700,
                        color: approach.energy < 0.4 ? '#16a34a' : approach.energy < 0.6 ? '#ca8a04' : '#dc2626'
                      }}>
                        {approach.energy}
                      </div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '2px' }}>Effectiveness</div>
                      <div style={{ fontSize: '20px', fontWeight: 700, color: '#16a34a' }}>
                        {(approach.effectiveness * 100).toFixed(0)}%
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {teachingQuality.by_approach && teachingQuality.by_approach.length > 0 && (
              <div style={{
                marginTop: '20px',
                padding: '16px',
                backgroundColor: '#eff6ff',
                borderRadius: '8px',
                border: '1px solid #3b82f6'
              }}>
                <div style={{ fontSize: '14px', color: '#1e40af', lineHeight: '1.6' }}>
                  <strong>Insight:</strong> {
                    teachingQuality.by_approach[0].energy < 0.3
                      ? `${teachingQuality.by_approach[0].approach.replace(/_/g, ' ')} is working exceptionally well across your school. Consider sharing this as a best practice.`
                      : teachingQuality.summary.avg_energy > 0.6
                      ? 'Your school shows higher energy scores than ideal. Consider professional development focused on student-centered teaching methods.'
                      : `${teachingQuality.by_approach[0].approach.replace(/_/g, ' ')} shows the best results. Continue emphasizing this approach while refining others.`
                  }
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Re-teach Prevention ROI */}
      {reteachROI && reteachROI.summary && (
        <div style={{
          backgroundColor: 'white',
          padding: '32px',
          borderRadius: '12px',
          border: '1px solid #e5e7eb',
          marginBottom: '32px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#1f2937', marginBottom: '8px' }}>
            Re-teach Prevention ROI
          </h3>
          <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '24px' }}>
            Time savings from catching common misconceptions early
          </p>

          {/* ROI Summary */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px', marginBottom: '28px' }}>
            <div style={{ padding: '20px', backgroundColor: '#f0fdf4', borderRadius: '8px', border: '1px solid #22c55e' }}>
              <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px', fontWeight: 600 }}>
                Time Saved
              </div>
              <div style={{ fontSize: '32px', fontWeight: 700, color: '#16a34a' }}>
                {Math.floor(reteachROI.summary.total_time_saved_minutes / 60)}h {reteachROI.summary.total_time_saved_minutes % 60}m
              </div>
              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                {reteachROI.summary.students_helped} students helped
              </div>
            </div>

            <div style={{ padding: '20px', backgroundColor: '#f0fdf4', borderRadius: '8px', border: '1px solid #22c55e' }}>
              <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px', fontWeight: 600 }}>
                Average ROI
              </div>
              <div style={{ fontSize: '32px', fontWeight: 700, color: '#16a34a' }}>
                {reteachROI.summary.avg_roi}x
              </div>
              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                time saved vs invested
              </div>
            </div>

            <div style={{ padding: '20px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
              <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px', fontWeight: 600 }}>
                Misconceptions Caught
              </div>
              <div style={{ fontSize: '32px', fontWeight: 700, color: '#4F46E5' }}>
                {reteachROI.summary.total_misconceptions}
              </div>
              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                last 30 days
              </div>
            </div>

            <div style={{ padding: '20px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
              <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px', fontWeight: 600 }}>
                Teacher Adoption
              </div>
              <div style={{ fontSize: '32px', fontWeight: 700, color: '#4F46E5' }}>
                {reteachROI.teacher_adoption.adoption_pct}%
              </div>
              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                {reteachROI.teacher_adoption.using_feature} of {reteachROI.teacher_adoption.total_teachers} teachers
              </div>
            </div>
          </div>

          {/* Top Re-teach Wins */}
          {reteachROI.top_wins && reteachROI.top_wins.length > 0 && (
            <div>
              <h4 style={{ fontSize: '15px', fontWeight: 600, color: '#1f2937', marginBottom: '16px' }}>
                Top Re-teach Wins
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {reteachROI.top_wins.map((win, index) => (
                  <div
                    key={index}
                    style={{
                      padding: '16px 20px',
                      backgroundColor: '#f9fafb',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '14px', fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
                        {win.topic}
                      </div>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                        {win.misconception_text}
                      </div>
                      <div style={{ fontSize: '12px', color: TappyBrandColors.semantic.success }}>
                        {win.students_affected} students • {win.time_saved_minutes} minutes saved
                      </div>
                    </div>
                    <div style={{
                      padding: '6px 12px',
                      backgroundColor: win.priority === 'critical' ? '#fef2f2' : win.priority === 'high' ? '#fef9e7' : '#f0fdf4',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: 600,
                      color: win.priority === 'critical' ? '#dc2626' : win.priority === 'high' ? '#ca8a04' : '#16a34a',
                      textTransform: 'uppercase'
                    }}>
                      {win.priority}
                    </div>
                  </div>
                ))}
              </div>

              <div style={{
                marginTop: '20px',
                padding: '16px',
                backgroundColor: '#f0fdf4',
                borderRadius: '8px',
                border: '1px solid #22c55e'
              }}>
                <div style={{ fontSize: '14px', color: '#15803d', lineHeight: '1.6' }}>
                  <strong>Business Impact:</strong> Your teachers saved <strong>{Math.floor(reteachROI.summary.total_time_saved_minutes / 60)} hours</strong> this month by catching common mistakes early.
                  That's equivalent to <strong>{(reteachROI.summary.total_time_saved_minutes / (40 * 60)).toFixed(1)} FTE</strong> at zero additional cost.
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Common Questions */}
      <div style={{
        backgroundColor: 'white',
        padding: '32px',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        marginBottom: '32px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#1f2937', marginBottom: '20px' }}>
          Most Common Student Questions
        </h3>

        {common_questions && common_questions.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {common_questions.slice(0, 10).map((item, index) => (
              <div
                key={index}
                style={{
                  padding: '16px 20px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '14px', color: '#1f2937', fontWeight: 500, marginBottom: '4px' }}>
                    {item.question_text || item.topic}
                  </div>
                  {item.subject && (
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>
                      Subject: {item.subject}
                    </div>
                  )}
                </div>
                <div style={{ textAlign: 'right', marginLeft: '20px' }}>
                  <div style={{ fontSize: '24px', fontWeight: 700, color: '#4F46E5' }}>
                    {item.count}
                  </div>
                  <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                    students
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p style={{ color: '#6b7280', textAlign: 'center', padding: '20px' }}>
            No question data available yet
          </p>
        )}
      </div>

      {/* Struggling Topics */}
      <div style={{
        backgroundColor: 'white',
        padding: '32px',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#1f2937', marginBottom: '20px' }}>
          Topics Students Struggle With
        </h3>

        {struggling_topics && struggling_topics.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {struggling_topics.slice(0, 8).map((item, index) => (
              <div
                key={index}
                style={{
                  padding: '16px 20px',
                  backgroundColor: '#fef3c7',
                  borderRadius: '8px',
                  border: '1px solid #fbbf24',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '14px', color: '#78350f', fontWeight: 500, marginBottom: '4px' }}>
                    {item.topic_name}
                  </div>
                  {item.subject && (
                    <div style={{ fontSize: '12px', color: '#92400e' }}>
                      {item.subject} • Avg mastery: {item.avg_mastery_score ? `${(item.avg_mastery_score * 100).toFixed(0)}%` : 'N/A'}
                    </div>
                  )}
                </div>
                <div style={{ textAlign: 'right', marginLeft: '20px' }}>
                  <div style={{ fontSize: '24px', fontWeight: 700, color: '#f59e0b' }}>
                    {item.struggling_students_count}
                  </div>
                  <div style={{ fontSize: '11px', color: '#92400e' }}>
                    students
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p style={{ color: '#6b7280', textAlign: 'center', padding: '20px' }}>
            No struggling topics identified yet
          </p>
        )}

        <div style={{
          marginTop: '20px',
          padding: '16px',
          backgroundColor: '#dbeafe',
          borderRadius: '8px',
          border: '1px solid #3b82f6'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: '8px',
              backgroundColor: TappyBrandColors.primary.skyBlue,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '16px',
              color: 'white',
              flexShrink: 0
            }}>i</div>
            <div style={{ fontSize: '14px', color: TappyBrandColors.primary.darkTeal }}>
              <strong>Intervention Opportunity:</strong> These topics may benefit from targeted re-teaching or additional resources.
              Consider sharing this data with department leads.
            </div>
          </div>
        </div>
      </div>

      {/* Export Section - Tappy Brand Style */}
      <div style={{
        marginTop: '32px',
        padding: '20px',
        backgroundColor: TappyBrandColors.neutral.paleGray,
        border: `2px solid ${TappyBrandColors.primary.teal}`,
        borderLeft: `4px solid ${TappyBrandColors.primary.teal}`,
        borderRadius: '12px',
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        boxShadow: `0 4px 12px rgba(41, 122, 146, 0.15)`
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          borderRadius: '10px',
          backgroundColor: 'white',
          border: `2px solid ${TappyBrandColors.primary.teal}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        }}>
          <div style={{
            fontSize: '20px',
            fontWeight: 600,
            color: TappyBrandColors.primary.teal
          }}>📥</div>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{
            fontWeight: 600,
            color: TappyBrandColors.neutral.darkGray,
            marginBottom: '6px',
            fontSize: '15px'
          }}>
            Export Learning Analytics
          </div>
          <div style={{
            fontSize: '13px',
            color: TappyBrandColors.primary.brandBlue,
            lineHeight: '1.5'
          }}>
            Download comprehensive reports for stakeholders
          </div>
        </div>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <button
            onClick={async () => {
              try {
                const response = await axios.post(
                  buildApiUrl('export/analytics'),
                  { time_period: timeRange },
                  { responseType: 'blob' }
                );
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', response.headers['content-disposition']?.split('filename=')[1] || 'analytics_export.csv');
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
              } catch (err) {
                console.error('Export error:', err);
                alert('Failed to export analytics. Please try again.');
              }
            }}
            style={{
              padding: '10px 20px',
              backgroundColor: TappyBrandColors.primary.teal,
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 600,
              flexShrink: 0,
              transition: 'all 200ms ease',
              boxShadow: `0 2px 8px rgba(41, 122, 146, 0.25)`
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = TappyBrandColors.primary.brightTeal;
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = TappyBrandColors.primary.teal;
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            CSV Report
          </button>
          <button
            onClick={async () => {
              try {
                const response = await axios.post(
                  buildApiUrl('export/board-report'),
                  { time_period: timeRange },
                  { responseType: 'blob' }
                );
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', response.headers['content-disposition']?.split('filename=')[1] || 'board_report.pdf');
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
              } catch (err) {
                console.error('Export error:', err);
                alert('Failed to generate board report. Please try again.');
              }
            }}
            style={{
              padding: '10px 20px',
              backgroundColor: 'white',
              color: TappyBrandColors.primary.teal,
              border: `2px solid ${TappyBrandColors.primary.teal}`,
              borderRadius: '10px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 600,
              flexShrink: 0,
              transition: 'all 200ms ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = TappyBrandColors.neutral.lightGray;
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'white';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            PDF Report
          </button>
        </div>
      </div>
    </div>
  );
}

export default SchoolAnalytics;
